import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { useProducer } from '@/hooks/useProducer';
import { useProducerTeam } from '@/hooks/useProducerTeam';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FarmlyAvatar } from '@/components/farmly/FarmlyAvatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { ROLE_LABELS, type ProducerRole, type ProducerMember } from '@/types/team';

export default function ProducerTeamMemberPage() {
  const { farmId, memberId } = useParams<{ farmId: string; memberId: string }>();
  const navigate = useNavigate();
  const { producer, loading: producerLoading } = useProducer(farmId || '');
  const { members, loading: teamLoading, updateMember, removeMember } = useProducerTeam(farmId);
  
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Find the member from the list
  const member = useMemo(
    () => members.find((m) => m.id === memberId),
    [members, memberId]
  );

  // Local form state
  const [role, setRole] = useState<ProducerRole>(member?.role || 'viewer');
  const [isPublic, setIsPublic] = useState(member?.isPublic ?? false);
  const [title, setTitle] = useState(member?.title || '');
  const [bio, setBio] = useState(member?.bio || '');

  // Update local state when member changes
  useMemo(() => {
    if (member) {
      setRole(member.role);
      setIsPublic(member.isPublic ?? false);
      setTitle(member.title || '');
      setBio(member.bio || '');
    }
  }, [member]);

  if (!farmId || !memberId) return null;

  const isLoading = producerLoading || teamLoading;

  const handleSaveAccess = async () => {
    if (!member) return;
    setIsSaving(true);
    try {
      await updateMember(member.id, { role });
      toast.success('Åtkomst uppdaterad');
    } catch (error: any) {
      toast.error(error.message || 'Kunde inte uppdatera åtkomst');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!member) return;
    setIsSaving(true);
    try {
      await updateMember(member.id, { isPublic, title, bio });
      toast.success('Publik profil uppdaterad');
    } catch (error: any) {
      toast.error(error.message || 'Kunde inte uppdatera profil');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveClick = () => {
    if (false) { // Role-based removal guard handled by canManageRole
      toast.error('Ägaren kan inte tas bort');
      return;
    }
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!member) return;
    try {
      await removeMember(member.id);
      toast.success('Personen har tagits bort från gården');
      navigate(`/producer/farm/${farmId}/team`);
    } catch (error: any) {
      toast.error(error.message || 'Kunde inte ta bort personen');
    } finally {
      setRemoveDialogOpen(false);
    }
  };

  const getRoleBadgeVariant = (r: ProducerRole) => {
    switch (r) {
      case 'admin': return 'default';
      default: return 'outline';
    }
  };

  if (isLoading) {
    return (
      <ProducerLayout farmId={farmId} producerName={producer?.name} title="Laddar...">
        <div className="space-y-4">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-64" />
        </div>
      </ProducerLayout>
    );
  }

  if (!member) {
    return (
      <ProducerLayout farmId={farmId} producerName={producer?.name} title="Hittas ej">
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Personen kunde inte hittas.</p>
          <Button variant="outline" onClick={() => navigate(`/producer/farm/${farmId}/team`)}>
            Tillbaka till team
          </Button>
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout
      farmId={farmId}
      producerName={producer?.name}
      title={member.name || member.email}
    >
      <div className="space-y-6">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2"
          onClick={() => navigate(`/producer/farm/${farmId}/team`)}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Tillbaka
        </Button>

        {/* Header with avatar and badges */}
        <div className="flex items-start gap-4">
          <FarmlyAvatar
            imageUrl={member.avatarUrl}
            initials={(member.name || member.email).slice(0, 2).toUpperCase()}
            alt={member.name || member.email}
            size="lg"
          />
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{member.name || member.email}</h1>
            {member.name && (
              <p className="text-muted-foreground">{member.email}</p>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge variant={getRoleBadgeVariant(member.role)}>
                {ROLE_LABELS[member.role]}
              </Badge>
              <Badge variant="outline">Aktiv</Badge>
              {member.isPublic && (
                <Badge variant="secondary">Syns publikt</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="access" className="space-y-6">
          <TabsList>
            <TabsTrigger value="access">Åtkomst</TabsTrigger>
            <TabsTrigger value="profile">Publik profil</TabsTrigger>
            <TabsTrigger value="history">Historik</TabsTrigger>
          </TabsList>

          {/* Access Tab */}
          <TabsContent value="access" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Roll</CardTitle>
                <CardDescription>
                  Bestäm vilken åtkomst personen har till gårdens administration.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {false ? (
                  <p className="text-muted-foreground">
                    Ägaren har full åtkomst och kan inte ändras via detta gränssnitt.
                  </p>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="role">Välj roll</Label>
                      <Select value={role} onValueChange={(v) => setRole(v as ProducerRole)}>
                        <SelectTrigger id="role" className="w-full max-w-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Medvärd – Full åtkomst</SelectItem>
                          <SelectItem value="editor">Redaktör – Redigering</SelectItem>
                          <SelectItem value="viewer">Läsare – Endast visning</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleSaveAccess} disabled={isSaving || role === member.role}>
                      {isSaving ? 'Sparar...' : 'Spara roll'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Publik profil</CardTitle>
                <CardDescription>
                  Inställningar för hur personen visas på den publika gårdssidan.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="isPublic" className="font-medium">Visa personen publikt</Label>
                    <p className="text-sm text-muted-foreground">
                      Personens namn, titel och bild visas på gårdssidan.
                    </p>
                  </div>
                  <Switch
                    id="isPublic"
                    checked={isPublic}
                    onCheckedChange={setIsPublic}
                  />
                </div>

                {isPublic && (
                  <>
                    <Separator />
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="title">Titel</Label>
                        <Input
                          id="title"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder="t.ex. Gårdsägare, Produktionsansvarig"
                          className="max-w-md"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bio">Kort beskrivning</Label>
                        <Textarea
                          id="bio"
                          value={bio}
                          onChange={(e) => setBio(e.target.value)}
                          placeholder="Berätta lite om personen..."
                          maxLength={240}
                          className="max-w-md"
                          rows={3}
                        />
                        <p className="text-sm text-muted-foreground">{bio.length}/240 tecken</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Profilbild</Label>
                        <div className="flex items-center gap-4">
                          <FarmlyAvatar
                            imageUrl={member.avatarUrl}
                            initials={(member.name || member.email).slice(0, 2).toUpperCase()}
                            size="lg"
                          />
                          <Button variant="outline" size="sm" disabled>
                            Byt bild (kommer snart)
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <Button 
                  onClick={handleSaveProfile} 
                  disabled={isSaving}
                  className="mt-4"
                >
                  {isSaving ? 'Sparar...' : 'Spara profil'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Historik</CardTitle>
                <CardDescription>
                  Händelser kopplade till denna person.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  {member.createdAt && (
                    <li className="flex items-start gap-3">
                      <Calendar className="h-4 w-4 mt-1 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Gick med i teamet</p>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(member.createdAt), 'd MMMM yyyy', { locale: sv })}
                        </p>
                      </div>
                    </li>
                  )}
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <Calendar className="h-4 w-4 mt-1" />
                    <p className="text-sm italic">Mer historik kommer snart...</p>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Danger Zone */}
        <>
          <Separator />
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="text-destructive">Farlig zon</CardTitle>
            </CardHeader>
            <CardContent>
              <Button variant="destructive" onClick={handleRemoveClick}>
                Ta bort från gården
              </Button>
            </CardContent>
          </Card>
        </>
      </div>

      {/* Remove Confirmation Dialog */}
      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort person från gården</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort {member.name || member.email} från gården? 
              Personen förlorar all åtkomst.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ProducerLayout>
  );
}
